import { useMemo } from "react"
import { useQuery } from "urql"

import "../graphql/queries"

import {
  GetInventoryPalletGroupListDocument,
  type InventoryPalletGroup,
  InventoryPalletGroupsSortField,
  SortingOrder,
} from "@/graphql/codegen/graphql"

interface UseGetInventoryPalletGroupsParams {
  productExternalSystemId: string
  warehouseAreaExternalSystemId: string
  search?: string
  sortBy?: InventoryPalletGroupsSortField
  sortDirection?: SortingOrder
  aisleCodes?: string[]
}

interface UseGetInventoryPalletGroupsReturn {
  palletGroups: InventoryPalletGroup[]
  isFetching: boolean
  error: string | undefined
}

export const useGetInventoryPalletGroups = ({
  productExternalSystemId,
  warehouseAreaExternalSystemId,
  search,
  sortBy = InventoryPalletGroupsSortField.ExpirationDate,
  sortDirection = SortingOrder.Asc,
  aisleCodes,
}: UseGetInventoryPalletGroupsParams): UseGetInventoryPalletGroupsReturn => {
  const [{ data, fetching, error }] = useQuery({
    query: GetInventoryPalletGroupListDocument,
    variables: {
      input: {
        prodNbr: Number(productExternalSystemId),
        whsNbr: Number(warehouseAreaExternalSystemId),
        search,
        sortBy,
        sortDirection,
        aisleCodes,
        offset: 0,
        limit: 500,
      },
    },
  })

  const output = data?.inventory?.listPalletGroups

  const palletGroups = useMemo(() => {
    if (output?.__typename === "InventoryPalletGroupsSuccess") {
      return output.items as InventoryPalletGroup[]
    }
    return []
  }, [output])

  const apiError = useMemo(() => {
    if (error) return error.message
    if (output?.__typename === "InventoryPalletGroupsFailure") {
      return output.error.message
    }
    return undefined
  }, [output, error])

  return { palletGroups, isFetching: fetching, error: apiError }
}
